import Box from '@redbubble/design-system/src/Box';
import * as constants from '@redbubble/design-system/src/constants';
import Skeleton from '@redbubble/design-system/src/Skeleton';
import { AnalyticsItemsList } from '@redbubble/next-analytics/gtm';
import { usePageProps } from '@redbubble/next-provider';
import Head from 'next/head';
import { useEffect } from 'react';
import { InventoryItem } from 'types/shop-bff/types';
import { AddToListButton } from '../../../../components/AddToListButton';
import {
  MatureContentCover,
  MatureContentCoverStyle,
} from '../../../../components/MatureContentCover';
import { getPreloadImage } from '../../../../components/Picture/utils';
import { SearchUUIDStorageKey } from '../../../../utils/searchUUIDStorage';
import { useCurrentPreview } from '../../hooks/useCurrentPreview';
import { ProductPageProps } from '../../types';
import { LoadingBoundary } from '../LoadingBoundary';
import { ProductPreviewCarousel } from './ProductPreviewCarousel';
import { ProductPreviewImage } from './ProductPreviewImage';
import styles from './ProductPreviews.module.scss';
import type { Preview, SelectedPreviewState } from './types';

export interface ProductPreviewProps {
  currentInventoryItem: InventoryItem;
  previews: Preview[];
  artistName?: string;
  productCategoryName: string;
  workTitle?: string;
  matureContent?: boolean;
  initialPreview: SelectedPreviewState;
}

export const ProductPreview = ({
  artistName,
  currentInventoryItem,
  initialPreview,
  matureContent,
  previews,
  productCategoryName,
  workTitle,
}: ProductPreviewProps) => {
  const { listsInclusions } = usePageProps<ProductPageProps>();

  const { selectedPreview, setSelectedPreview } = useCurrentPreview();

  // biome-ignore lint/correctness/useExhaustiveDependencies: useExhaustiveDependencies
  useEffect(() => {
    const primaryPreview =
      previews.find(
        (preview) => preview.previewTypeId === selectedPreview?.previewTypeId
      ) ||
      previews.find((preview) => preview.previewTypeId === 'primary') ||
      previews[0];

    if (selectedPreview?.url !== primaryPreview?.url) {
      setSelectedPreview(primaryPreview);
    }
  }, [previews]);

  const formatsPreloadImage = initialPreview && getPreloadImage(initialPreview);

  return (
    <>
      {formatsPreloadImage && (
        <Head>
          <link
            rel="preload"
            href={formatsPreloadImage.url}
            as="image"
            type={formatsPreloadImage.mime}
          />
        </Head>
      )}
      <Box className={styles.container} data-testid="product-preview">
        <Box className={styles.addToList} padding="m">
          <LoadingBoundary
            loadingComponent={
              <Skeleton height={48} width={48} animated circular />
            }
          >
            <AddToListButton
              inventoryItem={currentInventoryItem}
              workId={String(currentInventoryItem.workId)}
              listsInclusions={listsInclusions}
              analyticsComponentID={AnalyticsItemsList.ProductPreview}
              analyticsIndex={0}
              searchUUIDStorageKey={SearchUUIDStorageKey.LocationPathname}
              size={constants.LARGE}
            />
          </LoadingBoundary>
        </Box>
        <MatureContentCover
          matureContent={matureContent}
          aspectRatioHeight={0.75}
          styleType={MatureContentCoverStyle.Default}
        >
          <Box className={styles.productPreview}>
            {previews.length > 0 && selectedPreview && (
              <>
                <ProductPreviewCarousel
                  previews={previews}
                  artistName={artistName || ''}
                  productCategoryName={productCategoryName}
                  workTitle={workTitle || ''}
                  selectedPreview={selectedPreview}
                  onChange={setSelectedPreview}
                />
                <LoadingBoundary
                  loadingComponent={
                    <Skeleton ratio={constants.RATIO_PORTRAIT} animated />
                  }
                >
                  <ProductPreviewImage
                    selectedPreview={selectedPreview}
                    previews={previews}
                    artistName={artistName || ''}
                    productCategoryName={productCategoryName}
                    workTitle={workTitle || ''}
                    onChange={setSelectedPreview}
                  />
                </LoadingBoundary>
              </>
            )}
          </Box>
        </MatureContentCover>
      </Box>
    </>
  );
};
